'use client';

import {
  type ChatMessage,
  ChatMessageType,
  type ChatOrderMessage,
  type ChatPictureMessage,
  type ChatPostMessage,
  type ChatTextMessage,
  type ChatVideoMessage,
} from '../types/ChatMessage';
import { ImageMessage } from './message/image';
import { OrderMessage } from './message/order';
import { PostMessage } from './message/post';
import { TextMessage } from './message/text';
import { UnsupportedMessage } from './message/unsupported';
import { VideoMessage } from './message/video';

interface Props {
  message: ChatMessage;
  currentUserId: string;
}

/**
 * Dispatcher for a single message in the chat detail. Picks the
 * per-type renderer; each one wraps itself in `MessageBubbleShell`
 * so alignment + timestamp + status indicator stay consistent.
 *
 * Product / live / system still fall through to the unsupported
 * renderer until those land.
 */
export function ChatMessageBubble({ message, currentUserId }: Props) {
  const isOwn = message.from.userId === currentUserId;

  switch (message.type) {
    case ChatMessageType.Text:
      return <TextMessage message={message as ChatTextMessage} isOwn={isOwn} />;
    case ChatMessageType.Picture:
      return (
        <ImageMessage message={message as ChatPictureMessage} isOwn={isOwn} />
      );
    case ChatMessageType.Video:
      return (
        <VideoMessage message={message as ChatVideoMessage} isOwn={isOwn} />
      );
    case ChatMessageType.Order:
      return (
        <OrderMessage
          message={message as ChatOrderMessage}
          isOwn={isOwn}
          currentUserId={currentUserId}
        />
      );
    case ChatMessageType.Post:
      return <PostMessage message={message as ChatPostMessage} isOwn={isOwn} />;
    default:
      return <UnsupportedMessage message={message} isOwn={isOwn} />;
  }
}
